/**
 * RoleCreationWizard — Assistant guidé de création de rôle (RBAC V2.3)
 *
 * 3 étapes pour composer un rôle métier en quelques clics :
 *   Étape 1 — Profil métier (Finance / Ops / Commercial / Terrain / Audit)
 *   Étape 2 — Niveau hiérarchique (Junior / Senior / Lead)
 *   Étape 3 — Cloisons supplémentaires (read-only, no-export, etc.)
 *
 * Génère le rôle par composition de packs + filtres de cloisons.
 */
import React, { useState, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Progress } from '../ui/progress';
import {
  Sparkles, Calculator, Truck, Briefcase, MapPin, Eye,
  ChevronRight, ChevronLeft, Check, Shield, Lock,
} from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ====================================================================
// CONFIGURATION DU WIZARD
// ====================================================================

const BUSINESS_PROFILES = [
  {
    id: 'finance',
    name: 'Finance / Comptabilité',
    description: 'Factures, paiements, audit, Cockpit CFO',
    icon: Calculator,
    color: '#0891b2',
    basePack: 'finance_full',
    suggestedName: 'Finance',
  },
  {
    id: 'ops',
    name: 'Opérations Bureau',
    description: 'Planification, contrats, devis, dispatching',
    icon: Truck,
    color: '#16a34a',
    basePack: 'workflow_ops',
    suggestedName: 'Opérations',
  },
  {
    id: 'commercial',
    name: 'Commercial / Ventes',
    description: 'Clients, devis, contrats, portefeuille',
    icon: Briefcase,
    color: '#ca8a04',
    basePack: 'commercial_plus',
    suggestedName: 'Commercial',
  },
  {
    id: 'field',
    name: 'Opérations Terrain',
    description: 'Bons signés, GPS, incidents, livraisons',
    icon: MapPin,
    color: '#9333ea',
    basePack: 'field',
    suggestedName: 'Terrain',
  },
  {
    id: 'audit',
    name: 'Audit / Observateur',
    description: 'Lecture seule sur tout + export',
    icon: Eye,
    color: '#64748b',
    basePack: 'readonly',
    suggestedName: 'Audit',
  },
];

const SENIORITY_LEVELS = [
  {
    id: 'junior',
    name: 'Junior',
    description: 'Périmètre réduit, exécutant',
    icon: '🌱',
    extraPacks: [],
    suggestedSuffix: 'Junior',
  },
  {
    id: 'senior',
    name: 'Senior',
    description: 'Périmètre complet du profil',
    icon: '🌿',
    extraPacks: [],
    suggestedSuffix: 'Senior',
  },
  {
    id: 'lead',
    name: 'Lead / Manager',
    description: 'Tous les pouvoirs + réassignation équipe + analytics',
    icon: '🌳',
    extraPacks: ['manager', 'lead_view'],
    suggestedSuffix: 'Lead',
  },
];

const CLOISONS = [
  {
    id: 'no_pricing',
    name: 'Masquer les prix/coûts',
    description: "Retire l'accès aux champs financiers (estimatedPrice, marges, etc.)",
    icon: Lock,
    filter: (perm) => !perm.includes('view_pricing') && !perm.includes('.pricing'),
  },
  {
    id: 'no_export',
    name: 'Pas d\'export',
    description: 'Bloque tous les exports CSV/Excel/ZIP',
    icon: Lock,
    filter: (perm) => !perm.endsWith('.export') && !perm.endsWith('.export_zip') && !perm.endsWith('.export_ohada'),
  },
  {
    id: 'no_delete',
    name: 'Pas de suppression',
    description: 'Retire toutes les permissions delete',
    icon: Lock,
    filter: (perm) => !perm.endsWith('.delete') && !perm.endsWith('.deactivate'),
  },
  {
    id: 'read_only',
    name: 'Lecture seule stricte',
    description: 'Aucune action de création/édition/suppression',
    icon: Lock,
    filter: (perm) => {
      const writeKeywords = ['.create', '.edit', '.delete', '.update', '.assign', '.approve', '.reject',
        '.send', '.send_statement', '.upload', '.regenerate', '.backfill', '.refund', '.bulk_', '.manage'];
      return !writeKeywords.some(k => perm.includes(k));
    },
  },
  {
    id: 'no_messaging',
    name: 'Pas d\'accès messagerie',
    description: 'Retire toutes les permissions messaging.*',
    icon: Lock,
    filter: (perm) => !perm.startsWith('messaging.'),
  },
];

// ====================================================================
// COMPOSANT
// ====================================================================

const RoleCreationWizard = ({ open, onClose, allPacks, allPermissions, onCreated, initialPackSlug = null }) => {
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState(null);
  const [seniority, setSeniority] = useState(null);
  const [cloisons, setCloisons] = useState([]);
  const [roleName, setRoleName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Pré-sélection si on arrive depuis un deep-link (ex: /admin/roles?fromPack=finance_full)
  // On retrouve le profil métier dont le basePack correspond au slug demandé
  // ou — fallback — on synthétise un profil temporaire à partir du pack.
  React.useEffect(() => {
    if (!open || !initialPackSlug) return;
    // 1. Match direct sur les profils prédéfinis
    const matched = BUSINESS_PROFILES.find(p => p.basePack === initialPackSlug);
    if (matched) {
      setProfile(matched);
      setRoleName(matched.suggestedName);
      return;
    }
    // 2. Fallback : on retrouve le pack et on construit un profil à la volée
    const pack = (allPacks || []).find(p => p.slug === initialPackSlug);
    if (pack) {
      setProfile({
        id: `pack_${pack.slug}`,
        name: pack.name,
        description: pack.description || `Template depuis le pack ${pack.name}`,
        icon: Sparkles,
        color: pack.color || '#6366f1',
        basePack: pack.slug,
        suggestedName: pack.name,
      });
      setRoleName(pack.name);
    }
  }, [open, initialPackSlug, allPacks]);

  // ====================================================================
  // Logique de composition
  // ====================================================================
  const composedPermissions = useMemo(() => {
    if (!profile || !seniority) return [];

    // 1. Collecte des packs à fusionner
    const packSlugs = [profile.basePack, ...seniority.extraPacks];
    let perms = new Set();
    packSlugs.forEach(slug => {
      const pack = (allPacks || []).find(p => p.slug === slug);
      if (pack) pack.permissions.forEach(p => perms.add(p));
    });

    // 2. Junior : sous-ensemble (50% des perms, garde les view + ses créations propres)
    //    Compatibilité V1 (.view.scope) ET V2 (.submodule.view)
    if (seniority.id === 'junior') {
      perms = new Set(
        Array.from(perms).filter(p =>
          p.includes('.view.') ||           // V1 : operations.view.all
          p.endsWith('.view') ||            // V2 : finance.cockpit.view, customer_success.view
          p.includes('.create') ||
          p === 'messaging.send' ||
          p === 'documents.upload' ||
          p === 'incidents.create' ||
          p === 'tracking.update_location'
        )
      );
    }

    // 3. Lead : enrichit avec view.all sur tout son domaine
    if (seniority.id === 'lead') {
      // Déjà ajouté via manager + lead_view packs
    }

    // 4. Application des cloisons (filtres)
    let result = Array.from(perms);
    cloisons.forEach(cId => {
      const c = CLOISONS.find(x => x.id === cId);
      if (c) result = result.filter(c.filter);
    });

    return result.sort();
  }, [profile, seniority, cloisons, allPacks]);

  const suggestedName = useMemo(() => {
    if (!profile || !seniority) return '';
    return `${profile.suggestedName} ${seniority.suggestedSuffix}`;
  }, [profile, seniority]);

  // ====================================================================
  // Actions
  // ====================================================================
  const next = () => setStep(s => Math.min(4, s + 1));
  const prev = () => setStep(s => Math.max(1, s - 1));
  const reset = () => {
    setStep(1); setProfile(null); setSeniority(null); setCloisons([]); setRoleName('');
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async () => {
    const name = roleName.trim() || suggestedName;
    if (!name) {
      toast({ title: 'Nom requis', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const slug = name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      const payload = {
        name,
        slug,
        description: `Rôle généré via Wizard — Profil ${profile.name}, Niveau ${seniority.name}` +
          (cloisons.length ? `, Cloisons : ${cloisons.join(', ')}` : ''),
        color: profile.color,
        icon: 'shield',
        permissions: composedPermissions,
      };
      const res = await axios.post(`${API}/rbac/roles`, payload);
      toast({
        title: 'Rôle créé via Wizard',
        description: `${res.data.name} — ${res.data.permissions.length} permissions composées.`,
      });
      reset();
      onCreated && onCreated(res.data);
      onClose();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.response?.data?.detail || "Échec de création",
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ====================================================================
  // Rendu
  // ====================================================================
  const stepTitles = ['Profil métier', 'Niveau hiérarchique', 'Cloisons & options', 'Nom & confirmation'];
  const progress = ((step - 1) / 3) * 100;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="role-wizard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-cyan-600" />
            Assistant de création de rôle
          </DialogTitle>
          <DialogDescription>
            Étape {step}/4 — {stepTitles[step - 1]}
          </DialogDescription>
          <Progress value={progress} className="mt-2" />
        </DialogHeader>

        <div className="py-4 min-h-[300px]">
          {/* ÉTAPE 1 — Profil métier */}
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4">
                Choisissez le <strong>profil métier principal</strong> du rôle. Cela définira la base de permissions.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {BUSINESS_PROFILES.map(p => {
                  const Icon = p.icon;
                  const selected = profile?.id === p.id;
                  return (
                    <Card
                      key={p.id}
                      onClick={() => setProfile(p)}
                      data-testid={`wizard-profile-${p.id}`}
                      className={`cursor-pointer p-4 transition-all hover:shadow-md border-2 ${
                        selected ? 'border-cyan-500 shadow-md' : 'border-transparent'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-3 rounded-lg" style={{ backgroundColor: `${p.color}20` }}>
                          <Icon className="w-6 h-6" style={{ color: p.color }} />
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold flex items-center gap-2">
                            {p.name}
                            {selected && <Check className="w-4 h-4 text-cyan-600" />}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">{p.description}</div>
                          <Badge variant="outline" className="mt-2 text-xs">📦 {p.basePack}</Badge>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* ÉTAPE 2 — Niveau hiérarchique */}
          {step === 2 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4">
                Définissez le <strong>niveau hiérarchique</strong>. Plus le niveau est élevé, plus le rôle est puissant.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {SENIORITY_LEVELS.map(s => {
                  const selected = seniority?.id === s.id;
                  return (
                    <Card
                      key={s.id}
                      onClick={() => setSeniority(s)}
                      data-testid={`wizard-seniority-${s.id}`}
                      className={`cursor-pointer p-4 transition-all hover:shadow-md border-2 text-center ${
                        selected ? 'border-cyan-500 shadow-md' : 'border-transparent'
                      }`}
                    >
                      <div className="text-4xl mb-2">{s.icon}</div>
                      <div className="font-semibold flex items-center justify-center gap-2">
                        {s.name}
                        {selected && <Check className="w-4 h-4 text-cyan-600" />}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{s.description}</div>
                      {s.extraPacks.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1 justify-center">
                          {s.extraPacks.map(p => <Badge key={p} variant="secondary" className="text-xs">+{p}</Badge>)}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* ÉTAPE 3 — Cloisons */}
          {step === 3 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4">
                Ajoutez des <strong>cloisons</strong> pour restreindre encore plus le rôle (optionnel).
              </p>
              <div className="space-y-2">
                {CLOISONS.map(c => {
                  const Icon = c.icon;
                  const selected = cloisons.includes(c.id);
                  return (
                    <Card
                      key={c.id}
                      onClick={() => setCloisons(prev =>
                        selected ? prev.filter(x => x !== c.id) : [...prev, c.id]
                      )}
                      data-testid={`wizard-cloison-${c.id}`}
                      className={`cursor-pointer p-3 transition-all hover:shadow-sm border-2 ${
                        selected ? 'border-amber-400 bg-amber-50' : 'border-transparent'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox checked={selected} onCheckedChange={() => {}} />
                        <Icon className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div className="flex-1">
                          <div className="font-medium text-sm">{c.name}</div>
                          <div className="text-xs text-gray-500">{c.description}</div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
              <div className="mt-4 p-3 bg-blue-50 rounded text-sm">
                <strong>Aperçu :</strong> Ce rôle aura <strong>{composedPermissions.length}</strong> permissions.
              </div>
            </div>
          )}

          {/* ÉTAPE 4 — Nom & confirmation */}
          {step === 4 && (
            <div className="space-y-4">
              <div>
                <Label>Nom du rôle</Label>
                <Input
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  placeholder={suggestedName}
                  data-testid="wizard-role-name-input"
                />
                <div className="text-xs text-gray-500 mt-1">
                  Suggéré : <button onClick={() => setRoleName(suggestedName)} className="underline text-cyan-600" type="button">{suggestedName}</button>
                </div>
              </div>

              <Card className="p-4 bg-gradient-to-br from-cyan-50 to-blue-50">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-cyan-600" />
                  Récapitulatif
                </h4>
                <div className="space-y-1 text-sm">
                  <div>📋 <strong>Profil :</strong> {profile?.name}</div>
                  <div>🎚️ <strong>Niveau :</strong> {seniority?.name}</div>
                  <div>🔒 <strong>Cloisons :</strong> {cloisons.length === 0 ? 'Aucune' : cloisons.join(', ')}</div>
                  <div>⚡ <strong>Permissions composées :</strong> <Badge variant="secondary">{composedPermissions.length}</Badge></div>
                </div>

                {/* Warning si 0 perms — explique pourquoi le bouton est grisé */}
                {composedPermissions.length === 0 && (
                  <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
                    <div className="font-medium mb-1">⚠️ 0 permission composée — bouton grisé</div>
                    <ul className="list-disc pl-4 space-y-0.5">
                      {(!allPacks || allPacks.length === 0) && (
                        <li>
                          <strong>Aucun pack de permissions trouvé.</strong> Exécutez{' '}
                          <code className="bg-amber-100 px-1 rounded">POST /api/rbac/init</code> côté backend
                          pour seeder les rôles et packs système (10 packs attendus).
                        </li>
                      )}
                      {allPacks && allPacks.length > 0 && profile?.basePack && !allPacks.find(p => p.slug === profile.basePack) && (
                        <li>
                          Le pack <code className="bg-amber-100 px-1 rounded">{profile.basePack}</code> n'existe pas
                          dans la base. Re-seeder via <code className="bg-amber-100 px-1 rounded">POST /api/rbac/init</code>.
                        </li>
                      )}
                      {allPacks && allPacks.length > 0 && allPacks.find(p => p.slug === profile?.basePack) && (
                        <li>
                          Le filtre du niveau "{seniority?.name}" a tout exclu. Essayez un niveau supérieur (Senior ou Lead)
                          ou retournez à l'étape 2.
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                <details className="mt-3">
                  <summary className="cursor-pointer text-xs text-cyan-700 hover:underline">
                    Voir les permissions ({composedPermissions.length})
                  </summary>
                  <div className="mt-2 max-h-32 overflow-y-auto text-xs font-mono space-y-0.5 bg-white p-2 rounded border">
                    {composedPermissions.length === 0 ? (
                      <div className="text-gray-400 italic">Aucune permission — voir avertissement ci-dessus</div>
                    ) : (
                      composedPermissions.map(p => <div key={p}>{p}</div>)
                    )}
                  </div>
                </details>
              </Card>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between items-center">
          <div className="text-xs text-gray-400">Étape {step}/4</div>
          <div className="flex gap-2">
            {step > 1 && (
              <Button variant="outline" onClick={prev} data-testid="wizard-prev-btn">
                <ChevronLeft className="w-4 h-4 mr-1" />
                Précédent
              </Button>
            )}
            {step < 4 && (
              <Button
                onClick={next}
                disabled={(step === 1 && !profile) || (step === 2 && !seniority)}
                className="bg-cyan-600 hover:bg-cyan-700"
                data-testid="wizard-next-btn"
              >
                Suivant
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            {step === 4 && (
              <Button
                onClick={handleSubmit}
                disabled={submitting || composedPermissions.length === 0}
                className="bg-cyan-600 hover:bg-cyan-700"
                data-testid="wizard-create-btn"
              >
                <Sparkles className="w-4 h-4 mr-1" />
                {submitting ? 'Création…' : 'Créer le rôle'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RoleCreationWizard;
